import Image, { type ImageOptions } from '@tiptap/extension-image';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { resolveSignedUrl, type StorageBucket } from '@/shared/services/storageService';
import { getLiveEditor } from '@/shared/utils/tiptapEditor';

function readTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readCssWidth(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `${Math.floor(value)}px`;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) return `${trimmed}px`;
    return trimmed;
  }

  return null;
}

type Alignment = 'left' | 'center' | 'right';
const HANDLE_DIRECTIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const;
const MIN_WIDTH_PX = 50;

function applyNodeAttrs(
  node: ProseMirrorNode,
  wrapper: HTMLElement,
  container: HTMLElement,
  img: HTMLImageElement,
  shimmer: HTMLElement
) {
  const attrs = node.attrs ?? {};
  const alignment: Alignment =
    attrs.alignment === 'left' || attrs.alignment === 'right' ? attrs.alignment : 'center';
  const width = readCssWidth(attrs.width);

  wrapper.style.justifyContent = alignment;
  container.style.width = width || 'fit-content';

  const src = readTrimmedString(attrs.src);
  const storagePath = readTrimmedString(attrs.storagePath);

  // If storage path exists but no src yet, show shimmer (URL will be resolved async)
  if (storagePath && !src) {
    img.style.display = 'none';
    shimmer.style.display = 'block';
  } else {
    shimmer.style.display = 'none';
    img.style.display = 'block';
    if (img.getAttribute('src') !== src) img.src = src;
  }

  img.alt = readTrimmedString(attrs.alt);
  img.title = readTrimmedString(attrs.title);
}

const DocumentImageExtension = Image.extend({
  name: 'image',

  addOptions() {
    const parentOptions = this.parent?.() as Partial<ImageOptions> | undefined;

    return {
      inline: parentOptions?.inline ?? false,
      allowBase64: parentOptions?.allowBase64 ?? false,
      HTMLAttributes: parentOptions?.HTMLAttributes ?? {},
      resize: false,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      alignment: {
        default: 'center',
        parseHTML: (element: HTMLElement) => element.getAttribute('alignment') || 'center',
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.alignment || attributes.alignment === 'center') return {};
          return { alignment: attributes.alignment };
        },
      },
      width: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.style.width || element.getAttribute('width') || null,
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.width) return {};
          return { style: `width: ${attributes.width}` };
        },
      },
      id: {
        default: null,
        rendered: false,
      },
      storageBucket: {
        default: null,
        rendered: false,
      },
      storagePath: {
        default: null,
        rendered: false,
      },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      // Build DOM: wrapper > container > img + shimmer + resize handles
      const wrapper = document.createElement('div');
      wrapper.className = 'image-plus-wrapper';
      wrapper.style.display = 'flex';
      wrapper.style.width = '100%';

      const container = document.createElement('div');
      container.className = 'image-plus-container';
      container.style.position = 'relative';
      container.style.lineHeight = '0';

      const img = document.createElement('img');
      img.className = 'image-plus-img';
      img.style.width = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';

      // Shimmer placeholder for storage images loading their signed URL
      const shimmer = document.createElement('div');
      shimmer.className = 'image-plus-shimmer';
      shimmer.style.display = 'none';
      shimmer.style.width = '100%';
      shimmer.style.height = '200px';
      shimmer.style.borderRadius = '4px';
      shimmer.style.background = 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)';
      shimmer.style.backgroundSize = '200% 100%';
      shimmer.style.animation = 'image-shimmer 1.5s infinite';

      container.appendChild(img);
      container.appendChild(shimmer);

      // Resize handles
      const handles: HTMLElement[] = [];
      for (const direction of HANDLE_DIRECTIONS) {
        const handle = document.createElement('div');
        handle.className = 'image-resize-handle';
        handle.dataset.direction = direction;
        handles.push(handle);
        container.appendChild(handle);
      }

      wrapper.appendChild(container);

      // Apply initial attributes
      let currentNode = node;
      applyNodeAttrs(currentNode, wrapper, container, img, shimmer);

      let storageResolveRequestId = 0;

      const showResolvedStorageUrl = (storagePath: string, url: string) => {
        img.src = url;
        img.dataset.resolvedStoragePath = storagePath;
        img.style.display = 'block';
        shimmer.style.display = 'none';
      };

      const resolveStorageImageUrl = (storageBucket: StorageBucket, storagePath: string) => {
        if (img.dataset.resolvedStoragePath === storagePath && img.getAttribute('src')) {
          img.style.display = 'block';
          shimmer.style.display = 'none';
          return;
        }

        const requestId = ++storageResolveRequestId;
        resolveSignedUrl(storageBucket, storagePath)
          .then((url) => {
            if (requestId !== storageResolveRequestId) return;
            showResolvedStorageUrl(storagePath, url);
          })
          .catch(() => {
            if (requestId !== storageResolveRequestId) return;
            delete img.dataset.resolvedStoragePath;
            shimmer.style.display = 'none';
            img.style.display = 'block';
            img.alt = 'Failed to load image';
            img.src = '';
          });
      };

      // Resolve storage URL if needed
      const storagePath = readTrimmedString(node.attrs?.storagePath);
      const storageBucket = readTrimmedString(node.attrs?.storageBucket) as StorageBucket | '';
      const initialSrc = readTrimmedString(node.attrs?.src);
      if (storagePath && storageBucket && !initialSrc) {
        resolveStorageImageUrl(storageBucket, storagePath);
      } else if (storagePath && initialSrc) {
        img.dataset.resolvedStoragePath = storagePath;
      }

      // Resize state
      let resizing = false;

      function startResize(e: PointerEvent) {
        const instance = getLiveEditor(editor);
        if (!instance?.isEditable) return;
        e.preventDefault();
        e.stopPropagation();
        resizing = true;

        const handle = e.currentTarget as HTMLElement;
        handle.setPointerCapture(e.pointerId);

        const startX = e.clientX;
        const startPxWidth = container.offsetWidth;
        const maxPxWidth = wrapper.offsetWidth;
        const isLeft = (handle.dataset.direction ?? '').includes('left');

        const onMove = (ev: PointerEvent) => {
          const delta = isLeft ? startX - ev.clientX : ev.clientX - startX;
          const newPx = Math.max(MIN_WIDTH_PX, Math.min(startPxWidth + delta, maxPxWidth));
          container.style.width = `${newPx}px`;
        };

        const onUp = (ev: PointerEvent) => {
          handle.releasePointerCapture(ev.pointerId);
          handle.removeEventListener('pointermove', onMove);
          handle.removeEventListener('pointerup', onUp);
          resizing = false;

          const finalPx = container.offsetWidth;
          const pct = Math.max(1, Math.round((finalPx / maxPxWidth) * 100));
          const widthValue = `${pct}%`;
          container.style.width = widthValue;

          const pos = getPos();
          const nextInstance = getLiveEditor(editor);
          if (pos != null && nextInstance) {
            nextInstance
              .chain()
              .setNodeSelection(pos)
              .updateAttributes('image', { width: widthValue })
              .run();
          }
        };

        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
      }

      for (const handle of handles) {
        handle.addEventListener('pointerdown', startResize);
      }

      return {
        dom: wrapper,

        update(updatedNode: ProseMirrorNode) {
          if (updatedNode.type !== currentNode.type) return false;
          currentNode = updatedNode;
          applyNodeAttrs(currentNode, wrapper, container, img, shimmer);

          // Re-resolve storage URL if storagePath changed or src was cleared
          const newStoragePath = readTrimmedString(updatedNode.attrs?.storagePath);
          const newStorageBucket = readTrimmedString(updatedNode.attrs?.storageBucket) as
            | StorageBucket
            | '';
          const newSrc = readTrimmedString(updatedNode.attrs?.src);
          if (newStoragePath && newStorageBucket && !newSrc) {
            resolveStorageImageUrl(newStorageBucket, newStoragePath);
          } else if (newStoragePath && newSrc) {
            storageResolveRequestId += 1;
            img.dataset.resolvedStoragePath = newStoragePath;
          } else {
            storageResolveRequestId += 1;
            delete img.dataset.resolvedStoragePath;
          }

          return true;
        },

        selectNode() {
          container.classList.add('image-plus-selected');
        },

        deselectNode() {
          container.classList.remove('image-plus-selected');
        },

        ignoreMutation() {
          return true;
        },

        stopEvent(event: Event) {
          if (resizing) return true;
          const target = event.target;
          if (target instanceof HTMLElement && target.classList.contains('image-resize-handle')) {
            return true;
          }
          return false;
        },

        destroy() {
          for (const handle of handles) {
            handle.removeEventListener('pointerdown', startResize);
          }
        },
      };
    };
  },
});

export default DocumentImageExtension;
